import { Fragment } from 'react';
import { DisplayField, GuestProfile, PreSelectedField } from '@mews-commander/guest-profile';
import { lt } from '@mews/framework';
import { Optional } from '@mews/std';
import { Container, EmptyState, Grid, RadioButton, Stack, StackItem, Typography } from '@optimus-web/core';
import { matchAndMergeTestIds } from '../../../../../../constants';
import { getFieldValue } from '../getFieldValue';
import { CellLayout, EmptyStateSelect, GridHeader, GridHeaderProps, LabelCell, ValueComponent } from './components';

export interface HandleChangeParams {
    fieldName: DisplayField;
    value: Optional<string>;
    profileId: string;
}

export type ProfileIdsRecord = Record<DisplayField, { profileId: string }>;

export interface FieldsGridProps extends Pick<GridHeaderProps, 'mergeDisabled' | 'onSelectAll' | 'selectedProfileId'> {
    handleChange: (params: HandleChangeParams) => void;
    leftProfile: GuestProfile;
    onChangeEmptySelect: (value: string) => void;
    preSelectedFields: Array<PreSelectedField>;
    rightProfile: GuestProfile;
    targetId: string;
    profileIdsSelectedRecord: ProfileIdsRecord;
}

export const FieldsGrid = ({
    onSelectAll,
    handleChange,
    leftProfile,
    mergeDisabled,
    onChangeEmptySelect,
    preSelectedFields,
    rightProfile,
    selectedProfileId,
    targetId,
    profileIdsSelectedRecord,
}: FieldsGridProps) => (
    <Container display="block" padding={['100', '0', '500', '0']} width="100%" align="center" overflowX="auto">
        {preSelectedFields.length > 0 ? (
            <Grid
                templateColumns="20% 1fr 1fr"
                columnGap="200"
                rowGap="0"
                templateRows={`repeat(${preSelectedFields.length + 1}, auto)`}
            >
                <GridHeader
                    mergeDisabled={mergeDisabled}
                    selectedProfileId={selectedProfileId}
                    leftProfileId={leftProfile.id}
                    rightProfileId={rightProfile.id}
                    onSelectAll={onSelectAll}
                />
                {preSelectedFields.map(({ field: fieldName }) => {
                    if (!Object.values(DisplayField).includes(fieldName)) return null;

                    const leftValue = getFieldValue({ fieldName, profile: leftProfile });
                    const rightValue = getFieldValue({ fieldName, profile: rightProfile });

                    if (leftValue === undefined && rightValue === undefined) return null;

                    return (
                        <Fragment key={fieldName}>
                            <LabelCell fieldName={fieldName} />
                            <CellLayout selected={profileIdsSelectedRecord[fieldName]?.profileId === leftProfile.id}>
                                <RadioButton
                                    checked={profileIdsSelectedRecord[fieldName]?.profileId === leftProfile.id}
                                    disabled={mergeDisabled}
                                    data-test-id={`${matchAndMergeTestIds.selectField}-${leftProfile.id}-${fieldName}`}
                                    label={
                                        <ValueComponent
                                            type={fieldName}
                                            value={leftValue}
                                            loyaltyMembership={leftProfile.loyaltyMembership}
                                        />
                                    }
                                    name={fieldName}
                                    onChange={(e) =>
                                        handleChange({
                                            fieldName,
                                            value: e.target.value || null,
                                            profileId: leftProfile.id,
                                        })
                                    }
                                    value={leftValue || ''}
                                />
                            </CellLayout>
                            <CellLayout selected={profileIdsSelectedRecord[fieldName]?.profileId === rightProfile.id}>
                                <RadioButton
                                    checked={profileIdsSelectedRecord[fieldName]?.profileId === rightProfile.id}
                                    disabled={mergeDisabled}
                                    data-test-id={`${matchAndMergeTestIds.selectField}-${rightProfile.id}-${fieldName}`}
                                    label={
                                        <ValueComponent
                                            type={fieldName}
                                            value={rightValue}
                                            loyaltyMembership={rightProfile.loyaltyMembership}
                                        />
                                    }
                                    name={fieldName}
                                    onChange={(e) =>
                                        handleChange({
                                            fieldName,
                                            value: e.target.value || null,
                                            profileId: rightProfile.id,
                                        })
                                    }
                                    value={rightValue || ''}
                                />
                            </CellLayout>
                        </Fragment>
                    );
                })}
            </Grid>
        ) : (
            <Stack vertical spacing="300" itemAlignment="center">
                <EmptyState
                    primaryText={
                        <Typography textStyle="bodyMediumStrong" variant="tertiary" as="div">
                            <Stack vertical>
                                {lt('AccountMergeConflictingFieldsEmptyLabel')}
                                {lt('AccountMergeConflictingFieldsEmptyLowerLabel')}
                            </Stack>
                        </Typography>
                    }
                    size="medium"
                />
                <StackItem grow maxWidth="680px">
                    <EmptyStateSelect
                        leftProfile={leftProfile}
                        rightProfile={rightProfile}
                        mergeDisabled={mergeDisabled}
                        selectedProfileId={targetId}
                        onChange={onChangeEmptySelect}
                    />
                </StackItem>
            </Stack>
        )}
    </Container>
);
